import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { cardBackground, upcomingDateTime } from '../../utils/utils';
import Icon, { ICONS, COLORS } from './icon';
import Badges from './badges';
import AvatarBubbleRow from './avatar-bubble-row';
import WaitingIndicator from './waiting-indicator';
import TellyCard from '../../images/telly-card.png';
import useHome from '../../hooks/use-home';
import EventMenu from './event-menu';
import { CreateOrUpdateMeeting } from '../../connection/meeting';
import { initialSettingsState } from '../../stores/reducers/settings';

interface Props {
	connected: boolean;
	event: any;
	featured?: boolean;
	joinGreenroom: Function;
	buttonDisabled?: boolean;
	isWaiting?: boolean;
	showSessionType: string;
}

export default function EventCard(props: Props) {
	const host = useSelector(
		(state: GreenroomState) => state.SessionReducer.host,
	);
	const liveChannels = useSelector(
		(state: GreenroomState) => state.ContentReducer.liveChannels,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const token = useSelector(
		(state: GreenroomState) => state.SessionReducer.token,
	);
	const showroomsTokens = useSelector(
		(state: GreenroomState) => state.SessionReducer.showroomsTokens,
	);

	const {
		connected,
		event,
		featured = false,
		joinGreenroom,
		buttonDisabled,
		isWaiting = false,
		showSessionType,
	} = props;

	const { handleEnterShowDetails, showDetailsButtonDisabled } = useHome();

	function buttonText(meeting: any) {
		if (!host) {
			if (liveChannels.includes(meeting?.meeting) || meeting?.entry_without_producer) {
				return featured ? 'Join Greenroom' : 'Join';
			} return 'Waiting for producer';
		}
		return featured ? 'Join Greenroom' : 'Join';
	}

	async function join(e: React.MouseEvent<HTMLButtonElement>) {
		e.stopPropagation();
		if (buttonDisabled || showDetailsButtonDisabled) return;

		if ((event.settings_json === '{}' || !event.settings_json) && appSignedInWith) {
			const showroomsToken = showroomsTokens?.accessToken!;
			event.settings_json = JSON.stringify(initialSettingsState);
			await CreateOrUpdateMeeting(
				token || 'n/a',
				event.meeting,
				event,
				appSignedInWith,
				event.standalone_title,
				undefined,
				undefined,
				showroomsToken,
				event.meeting_id,
			);
		}

		joinGreenroom(event);
	}

	function eventDetails(e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) {
		e.stopPropagation();
		if (buttonDisabled || showDetailsButtonDisabled) return;
		handleEnterShowDetails(event);
	}

	return (
		<div
			className={classNames(
				'card',
				{ 'pointer-events-none': buttonDisabled || showDetailsButtonDisabled },
			)}
			style={cardBackground(event, TellyCard, true, featured)}
			onClick={featured ? eventDetails : () => { }}
		>
			<div className="card-top">
				<div className="card-badges">
					<Badges
						start_time={event.start_time}
						end_time={event.end_time}
						show_session_type={showSessionType}
					/>
				</div>
				{featured
					&& <div className="card-presenters">
						<AvatarBubbleRow invites={event?.invites} size={'thirtytwo'} alignEnd={true} />
						<EventMenu event={event} />
					</div>
				}
			</div>
			<div className="card-bottom">
				<div className="card-bottom-meta">
					<h2>{event?.title}</h2>
					<h3>{upcomingDateTime(event?.start_time, event?.end_time, false, false)}</h3>
				</div>
				<div className="card-bottom-row">
					{connected && (
						<div className="card-buttons">
							{!isWaiting ? (
								<button disabled={buttonDisabled || showDetailsButtonDisabled} className={classNames('join-button', { presenter: !host, 'is-live': (liveChannels.includes(event?.meeting) || event?.entry_without_producer) })} onClick={(e) => join(e)}>
									<span>{buttonText(event)}</span>
								</button>
							) : (
								<div className="join-waiting">
									<span>You'll join when the producer lets you in </span><WaitingIndicator />
								</div>
							)}
							{host && featured && (
								<button disabled={buttonDisabled || showDetailsButtonDisabled} className="edit-button" onClick={eventDetails}>
									<span>Set up show</span>
								</button>
							)}
						</div>
					)}
					{featured
						&& <button disabled={buttonDisabled || showDetailsButtonDisabled}className="details-button">
							<Icon name={ICONS.ARROW_RIGHT} color={COLORS.WHITE} size={20} />
						</button>
					}
				</div>
			</div>
		</div>
	);
}
